import React from "react";
import {date} from "yup";
import {useTranslation} from "@unibuddy/intl";
import {View, Form, FormGroup, DateInput, Button, SubmitButton} from "@unibuddy/patron";
import {FormikHelpers, ErrorMessage} from "formik";
import {AuthFormError} from "../../../../components/AuthFormError/AuthFormError";
import {isFunction} from "../../../../util/isFunction";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_GENERIC_ERROR} from "../../../../translations/errorMessage";
import {
    INTL_CONTINUE_TO_SIGN_UP,
    INTL_DATE_OF_BIRTH,
    INTL_TODAY,
} from "../../../../translations/verifyAge";
import {INTL_DATE_OF_BIRTH_REQUIRED} from "../../../../translations/validations";

type AgeVerificationFormProps = {
    handleSubmit: (dob: Date) => Promise<void>;
};

export function AgeVerificationForm({handleSubmit}: AgeVerificationFormProps) {
    const {t} = useTranslation([
        IntlNamespaces.VERIFY_AGE,
        IntlNamespaces.VALIDATIONS,
        IntlNamespaces.ERROR_MESSAGE,
    ]);

    return (
        <Form
            initialValues={{dob: "", serverError: ""}}
            validationSchema={{
                dob: date().required(
                    t(
                        `${IntlNamespaces.VALIDATIONS}:${INTL_DATE_OF_BIRTH_REQUIRED.key}`,
                        INTL_DATE_OF_BIRTH_REQUIRED.defaultValue,
                    ),
                ),
            }}
            onSubmit={async (
                values: {
                    dob: Date;
                },
                {
                    setSubmitting,
                    setFieldError,
                }: FormikHelpers<{
                    dob: Date;
                }>,
            ) => {
                setSubmitting(true);
                try {
                    await handleSubmit(values.dob);
                } catch (error) {
                    setFieldError(
                        "serverError",
                        t(
                            `${IntlNamespaces.ERROR_MESSAGE}:${INTL_GENERIC_ERROR.key}`,
                            INTL_GENERIC_ERROR.defaultValue,
                        ),
                    );
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {(props: {errors: {serverError: string}}) => {
                const {errors} = props;
                return (
                    <View gap="medium-large">
                        <FormGroup
                            label={t(
                                `${IntlNamespaces.VERIFY_AGE}:${INTL_DATE_OF_BIRTH.key}`,
                                isFunction(INTL_DATE_OF_BIRTH.defaultValue)
                                    ? INTL_DATE_OF_BIRTH.defaultValue()
                                    : INTL_DATE_OF_BIRTH.defaultValue,
                                "Date of birth",
                            )}
                            w="100%"
                        >
                            {/* how does this have paddingbottom ? */}
                            <DateInput
                                id="dob"
                                name="dob"
                                showMonthDropdown
                                showYearDropdown
                                dateFormat="dd-MM-yyyy"
                                placeholder={"DD-MM-YYYY"}
                                maxDate={new Date()}
                                todayButton={
                                    <Button size="sm" clear>
                                        {t(
                                            `${IntlNamespaces.VERIFY_AGE}:${INTL_TODAY.key}`,
                                            INTL_TODAY.defaultValue,
                                        )}
                                    </Button>
                                }
                                required
                            />
                        </FormGroup>
                        {errors.serverError && (
                            <ErrorMessage name="serverError" component={AuthFormError} />
                        )}
                        <SubmitButton color="university" block size="lg">
                            {t(
                                `${IntlNamespaces.VERIFY_AGE}:${INTL_CONTINUE_TO_SIGN_UP.key}`,
                                INTL_CONTINUE_TO_SIGN_UP.defaultValue,
                            )}
                        </SubmitButton>
                    </View>
                );
            }}
        </Form>
    );
}
